// ONBOARDING-P1 (client-onboarding design, phase 1: the manual process end to end). Behind
// CLIENT_ONBOARDING=1 in the API and the worker (default off): while it is off every route here
// answers 404 and `operator.companies.create` refuses `plan_key`.
//
//   POST /v1/operator/companies  { …, plan_key }   (operator.ts) creating a client with a plan
//        opens its onboarding case and sends the owner the onboarding link at once. With no mail
//        provider configured the send is recorded as "would send" and the link is returned to the
//        operator ("Send this link to the owner").
//   GET  /v1/operator/companies/{company_id}/onboarding   the case for the operator: plan, status,
//        deadline, what is missing, the follow-up schedule and its outcomes, the timeline, and the
//        link while it has not been mailed.
//   GET  /v1/companies/{co}/onboarding/intake/{token}           the owner's intake, by the link
//   PUT  /v1/companies/{co}/onboarding/intake/{token}/steps/{step}   save one step
//   POST /v1/companies/{co}/onboarding/intake/{token}/submit     submit (every required answer in)
//
// The link routes have no principal: the token (`<case id>.<secret>`, SHA-256 at rest) is the
// credential, as a client survey's. Client-facing text speaks as the HTVA team.
//
// ONBOARDING-P2 (setup proposal, setup assistant, owner review per seat):
//   GET  /v1/operator/companies/{company_id}/onboarding/proposal        versions, decisions, approved
//   PUT  /v1/operator/companies/{company_id}/onboarding/proposal        edit the version under review
//   POST /v1/operator/companies/{company_id}/onboarding/proposal/send   freeze it for the client
//   GET  /v1/operator/companies/{company_id}/onboarding/assistants      HQ assistants, the current one
//   PUT  /v1/operator/companies/{company_id}/onboarding/assistant       assign one
//   POST /v1/operator/companies/{company_id}/onboarding/close           go-live or cancel: revokes
//   GET  /v1/companies/{co}/onboarding/intake/{token}/proposal          the sent version, for the owner
//   PUT  /v1/companies/{co}/onboarding/intake/{token}/proposal/seats/{seat_key}   approve/change/not now
// The owner's proposal routes go through the client's setup assistant: with no live assignment
// they answer 404.
import { z } from "zod";
import {
  DispatchItemClass,
  DispatchItemRoute,
  DispatchItemSource,
  DispatchQuarantineReason,
  OnboardingCaseStatus,
  OnboardingDelivery,
  OnboardingFollowupKind,
  OnboardingFollowupStatus,
  OnboardingReadinessCheck,
  OnboardingReviewKind,
  OnboardingReviewStatus,
  OnboardingSeatingStatus,
  OnboardingSeatingStep,
  SetupAssistantStatus,
  SetupProposalOrigin,
  SetupProposalStatus,
  SetupSeatDecision,
  SetupSeatKind,
  SetupSeatStart,
  TemplateTicketStatus,
} from "../enums";
import { BoundedJson, Slug, Timestamp, Uuid } from "../primitives";
import { FlowAnswer, FlowMissing, FlowProgress } from "./flows";
import { defineRoute } from "./route";

/** What the create answer carries about the case it opened. */
export const OnboardingOpened = z
  .object({
    case_id: Uuid,
    plan_key: Slug,
    due_at: Timestamp,
    delivery: OnboardingDelivery,
    /**
     * The onboarding link (a web path), present while it was not mailed: the operator sends it to
     * the owner by hand. Null once a mail provider sent it.
     */
    link: z.string().nullable(),
  })
  .meta({ id: "OnboardingOpened" });

/** The client row's onboarding summary in the operator workspace (null: no case, or the flag off). */
export const OnboardingCaseSummary = z
  .object({
    case_id: Uuid,
    plan_key: Slug,
    status: OnboardingCaseStatus,
    due_at: Timestamp,
    overdue: z.boolean(),
    link_delivery: OnboardingDelivery,
    /** Required intake answers still missing. */
    missing: z.int().min(0),
    percent: z.int().min(0).max(100),
    next_followup: z.object({ kind: OnboardingFollowupKind, due_at: Timestamp }).nullable(),
    /** The sequence ran out without a complete intake: the operator should call. */
    operator_alerted: z.boolean(),
    /** Lane A (§3.1): when all six readiness checks first held (never cleared). */
    ready_at: Timestamp.nullable(),
    /** Lane A (§3.1): the operator's handover; the case is done once it is set. */
    handover_at: Timestamp.nullable(),
  })
  .meta({ id: "OnboardingCaseSummary" });
export type OnboardingCaseSummary = z.infer<typeof OnboardingCaseSummary>;

/** ONBOARDING-P2: the client's HQ setup assistant, while onboarding is open. */
export const OnboardingAssistantRef = z
  .object({ agent_id: Uuid, name: z.string(), since: Timestamp })
  .meta({ id: "OnboardingAssistantRef" });

export const OnboardingCaseView = OnboardingCaseSummary.extend({
  company_id: Uuid,
  /** ONBOARDING-P2: when the operator closed the case (go-live or cancel). */
  ended_at: Timestamp.nullable(),
  /** ONBOARDING-P2: the live assignment (null once onboarding ended). */
  assistant: OnboardingAssistantRef.nullable(),
  /** ONBOARDING-P2: the owner's seat the answers moved to, once the owner held one. */
  intake_seat_id: Uuid.nullable(),
  owner: z.object({ email: z.string(), name: z.string() }),
  link_sent_at: Timestamp,
  link: z.string().nullable(),
  intake_completed_at: Timestamp.nullable(),
  missing_fields: z.array(FlowMissing),
  answers: z.record(z.string(), FlowAnswer),
  followups: z.array(
    z.object({
      kind: OnboardingFollowupKind,
      business_day: z.int().min(0),
      due_at: Timestamp,
      status: OnboardingFollowupStatus,
      done_at: Timestamp.nullable(),
    }),
  ),
  timeline: z.array(
    z.object({ kind: z.string(), at: Timestamp, detail: z.record(z.string(), z.unknown()) }),
  ),
}).meta({ id: "OnboardingCaseView" });
export type OnboardingCaseView = z.infer<typeof OnboardingCaseView>;

/** What the owner sees through the link. */
export const OnboardingIntakeView = z
  .object({
    company_name: z.string(),
    /** Who is asking: always the HTVA team's setup assistant, never an internal agent's name. */
    from: z.string(),
    intake_key: z.literal("collect_onboarding"),
    intake_version: z.int().min(1),
    status: OnboardingCaseStatus,
    due_at: Timestamp,
    answers: z.record(z.string(), FlowAnswer),
    saved_steps: z.array(z.string()),
    missing: z.array(FlowMissing),
    progress: FlowProgress,
  })
  .meta({ id: "OnboardingIntakeView" });
export type OnboardingIntakeView = z.infer<typeof OnboardingIntakeView>;

export const OnboardingTokenParams = z.object({
  co: Uuid,
  token: z.string().min(20).max(200),
});
export const OnboardingStepParams = OnboardingTokenParams.extend({
  step: z
    .string()
    .max(64)
    .regex(/^[a-z][a-z0-9_]{0,63}$/),
});
export const OnboardingStepSave = z
  .strictObject({ values: BoundedJson })
  .meta({ id: "OnboardingStepSave" });

// ---------------------------------------------------------------------------------------------
// ONBOARDING-P2: the setup proposal, the owner's review per seat, the HQ setup assistant.

const shortText = z.string().trim().min(1).max(400);
const seatKey = z
  .string()
  .max(64)
  .regex(/^[a-z][a-z0-9_-]{0,63}$/);

/** One proposed seat (a person or a digital employee). */
export const SetupSeat = z
  .strictObject({
    key: seatKey,
    title: z.string().trim().min(1).max(120),
    kind: SetupSeatKind,
    /** A published platform template for a DE seat; null for a person's seat. */
    template_key: Slug.nullable(),
    /** Helpers the seat works with (plain names). */
    helpers: z.array(shortText).max(10),
    /** 2-4 measures for a DE seat. */
    kpis: z.array(shortText).max(6),
    /** What the seat should achieve in its first 30 days. */
    first_goals: z.array(shortText).max(6),
    start: SetupSeatStart,
    /** The intake answers (field keys) the seat rests on. */
    evidence: z.array(z.string().max(64)).min(1).max(12),
  })
  .meta({ id: "SetupSeat" });

export const SetupProposalBody = z
  .strictObject({
    summary: z.string().trim().max(2000),
    seats: z.array(SetupSeat).max(20),
    first_goals: z.array(shortText).max(10),
    open_questions: z.array(shortText).max(10),
  })
  .meta({ id: "SetupProposalBody" });
export type SetupProposalBody = z.infer<typeof SetupProposalBody>;

export const SetupSeatDecisionView = z
  .object({
    seat_key: z.string(),
    decision: SetupSeatDecision,
    note: z.string().nullable(),
    decided_at: Timestamp,
  })
  .meta({ id: "SetupSeatDecisionView" });

/** The operator's view: every version, the decisions on the sent one, and what may be deployed. */
export const SetupProposalOperatorView = z
  .object({
    case_id: Uuid,
    versions: z.array(
      z.object({
        id: Uuid,
        version: z.int().min(1),
        status: SetupProposalStatus,
        origin: SetupProposalOrigin,
        draft_note: z.string().nullable(),
        edited_at: Timestamp.nullable(),
        sent_at: Timestamp.nullable(),
        body: SetupProposalBody,
      }),
    ),
    /** The version the client sees (null until one was sent). */
    sent_version: z.int().min(1).nullable(),
    decisions: z.array(SetupSeatDecisionView),
    /** The seats the owner approved: what the operator deploys, by hand. */
    approved_seats: z.array(SetupSeat),
    /** Seats the owner asked to change, with their notes. */
    change_requests: z.array(SetupSeatDecisionView),
  })
  .meta({ id: "SetupProposalOperatorView" });
export type SetupProposalOperatorView = z.infer<typeof SetupProposalOperatorView>;

/** What the owner is shown: the sent version only, presented by the HTVA team's setup assistant. */
export const SetupProposalClientView = z
  .object({
    company_name: z.string(),
    /** Always the HTVA team's setup assistant, never an internal agent's name. */
    from: z.string(),
    version: z.int().min(1),
    summary: z.string(),
    seats: z.array(
      z.object({
        key: z.string(),
        title: z.string(),
        kind: SetupSeatKind,
        helpers: z.array(z.string()),
        kpis: z.array(z.string()),
        first_goals: z.array(z.string()),
        start: SetupSeatStart,
        decision: SetupSeatDecisionView.nullable(),
      }),
    ),
    first_goals: z.array(z.string()),
    open_questions: z.array(z.string()),
    /**
     * ONBOARDING-P3: the call the setup assistant offers. `booking_url` is the platform's
     * booking-link setting (ONBOARDING_BOOKING_URL); null while it is not set: the owner replies
     * with times that suit them instead.
     */
    call: z.object({ booking_url: z.string().nullable() }),
  })
  .meta({ id: "SetupProposalClientView" });
export type SetupProposalClientView = z.infer<typeof SetupProposalClientView>;

export const SetupSeatDecide = z
  .strictObject({
    decision: SetupSeatDecision,
    note: z.string().trim().max(2000).optional(),
  })
  .meta({ id: "SetupSeatDecide" });

export const SetupAssistantList = z
  .object({
    assistants: z.array(
      z.object({
        id: Uuid,
        name: z.string(),
        status: SetupAssistantStatus,
        /** Clients it serves now. */
        clients: z.int().min(0),
      }),
    ),
    current: OnboardingAssistantRef.nullable(),
  })
  .meta({ id: "SetupAssistantList" });
export type SetupAssistantList = z.infer<typeof SetupAssistantList>;

export const SetupAssistantAssign = z
  .strictObject({ agent_id: Uuid })
  .meta({ id: "SetupAssistantAssign" });

export const OnboardingClose = z
  .strictObject({ outcome: z.enum(["live", "cancelled"]) })
  .meta({ id: "OnboardingClose" });

export const OnboardingSeatParams = OnboardingTokenParams.extend({ seat_key: seatKey });

// ---------------------------------------------------------------------------------------------
// ONBOARDING-P3: the chat presentation and call offer, the seating run, the DE allowance.

/** What the setup assistant posts in the client's support channel. */
export const OnboardingPresent = z
  .strictObject({ what: z.enum(["proposal", "call_offer"]) })
  .meta({ id: "OnboardingPresent" });

export const OnboardingPresented = z
  .object({
    what: z.enum(["proposal", "call_offer"]),
    /** The sent version presented. */
    version: z.int().min(1),
    channel_id: Uuid,
    /** Null when it was already posted (presenting again posts nothing). */
    message_id: Uuid.nullable(),
  })
  .meta({ id: "OnboardingPresented" });
export type OnboardingPresented = z.infer<typeof OnboardingPresented>;

/** The company's plan and its included digital employees (PLAN-DES). */
export const DeAllowance = z
  .object({
    plan_key: Slug.nullable(),
    plan_name: z.string().nullable(),
    /** Null: the plan sets no allowance (no limit). */
    included_des: z.int().min(0).nullable(),
    in_use: z.int().min(0),
    /** Places held by DE seats of the seating run that have no DE yet (§3.3). */
    reserved: z.int().min(0),
    remaining: z.int().min(0).nullable(),
  })
  .meta({ id: "DeAllowance" });
export type DeAllowance = z.infer<typeof DeAllowance>;

export const SeatingSeatView = z
  .object({
    seat_key: z.string(),
    title: z.string(),
    kind: SetupSeatKind,
    template_key: z.string().nullable(),
    status: OnboardingSeatingStatus,
    /**
     * The next step (the recovery contract): seat → provision → connect → activate → first_task
     * → ready (a person's seat: seat → ready). A retry resumes here.
     */
    step: OnboardingSeatingStep,
    /** Failed tries of the current step (at 3 the seat is failed). */
    attempts: z.int().min(0),
    /** Why it is refused, blocked or failed; what was made once deployed. */
    message: z.string().nullable(),
    /** The last step error, until a step goes through. */
    last_error: z.string().nullable(),
    /** Passed over until then: waiting on the worker or the runtime connection. */
    waiting_until: Timestamp.nullable(),
    ready_at: Timestamp.nullable(),
    seat_id: Uuid.nullable(),
    de_membership_id: Uuid.nullable(),
    ticket_id: Uuid.nullable(),
    done_at: Timestamp.nullable(),
  })
  .meta({ id: "SeatingSeatView" });

export const TemplateTicketView = z
  .object({
    id: Uuid,
    seat_key: z.string(),
    title: z.string(),
    wanted_template_key: z.string().nullable(),
    kpis: z.array(z.string()),
    first_goals: z.array(z.string()),
    status: TemplateTicketStatus,
    created_at: Timestamp,
  })
  .meta({ id: "TemplateTicketView" });

/** The seating run of a client (the operator's Seating tab). */
export const SeatingView = z
  .object({
    case_id: Uuid,
    /** The version the owner approved seats on (null until one was sent). */
    sent_version: z.int().min(1).nullable(),
    /** Seats the owner approved on it. */
    approved: z.int().min(0),
    allowance: DeAllowance,
    seats: z.array(SeatingSeatView),
    tickets: z.array(TemplateTicketView),
    /** Seats still to run (`pending` or `deploying`). */
    pending: z.int().min(0),
    /** Of those, the DE seats waiting on their set-up or runtime connection: run again shortly. */
    waiting: z.int().min(0),
    /** Whether "Deploy seats" has anything to (re)plan, and why not. */
    can_deploy: z.boolean(),
    blocked_reason: z.string().nullable(),
    /**
     * The `next` route: the seat it just moved and the step it reached (null otherwise, or when
     * nothing pending could move).
     */
    ran: z
      .object({
        seat_key: z.string(),
        status: OnboardingSeatingStatus,
        step: OnboardingSeatingStep,
      })
      .nullable(),
  })
  .meta({ id: "SeatingView" });
export type SeatingView = z.infer<typeof SeatingView>;

/**
 * Lane A (beta shared contracts §3.2): the six checks a case is handed over on, each derived from
 * its evidence, and the handover. `ready_at` is set the first time all six hold and never cleared.
 */
export const OnboardingReadinessView = z
  .object({
    case_id: Uuid,
    checks: z.array(
      z.object({
        key: OnboardingReadinessCheck,
        ok: z.boolean(),
        /** What holds, or what is missing (plain words, names and counts only). */
        detail: z.string(),
      }),
    ),
    checked_at: Timestamp.nullable(),
    ready_at: Timestamp.nullable(),
    handover_at: Timestamp.nullable(),
    /** Setup-assistant assignments ended by the handover (this call only). */
    revoked: z.int().min(0),
  })
  .meta({ id: "OnboardingReadinessView" });
export type OnboardingReadinessView = z.infer<typeof OnboardingReadinessView>;

// ---------------------------------------------------------------------------------------------
// ONBOARDING-P4: after go-live. The client's system address and what the HQ dispatcher did with
// each message to it; the 30-day review; the intake answers a DE reads for its seat.

/** The client's system address (`support+<slug>@<system domain>`), opened at the handover. */
export const SystemAddressView = z
  .object({ address: z.string(), opened_at: Timestamp, closed_at: Timestamp.nullable() })
  .meta({ id: "SystemAddressView" });

/** One message the HQ dispatcher took (operator view; never shown to the client). */
export const DispatchItemView = z
  .object({
    id: Uuid,
    received_at: Timestamp,
    source: DispatchItemSource,
    sender: z.string(),
    sender_verified: z.boolean(),
    /** The sender is the client's (its owner, or a person who accepted an invitation). */
    sender_known: z.boolean(),
    subject: z.string(),
    /** The client's words, quoted: data, never instructions. */
    quoted: z.string(),
    class: DispatchItemClass,
    /** The rules the class rests on. */
    reasons: z.array(z.string()),
    route: DispatchItemRoute,
    quarantine_reason: DispatchQuarantineReason.nullable(),
    /** The client's assigned operator it went to. */
    operator_user_id: Uuid.nullable(),
    /** Whether a note went to the client's internal maintenance log (read and log only). */
    noted: z.boolean(),
  })
  .meta({ id: "DispatchItemView" });

export const DispatchListView = z
  .object({
    company_id: Uuid,
    /** Null until the handover opened it. */
    address: SystemAddressView.nullable(),
    items: z.array(DispatchItemView),
  })
  .meta({ id: "DispatchListView" });
export type DispatchListView = z.infer<typeof DispatchListView>;

const ReviewKpi = z.object({
  name: z.string(),
  target: z.number().nullable(),
  actual: z.number().nullable(),
  direction: z.string(),
});

/** What the 30-day review collected. */
export const OnboardingReviewMetrics = z
  .object({
    window: z.object({ from: Timestamp, to: Timestamp }),
    seats: z.array(
      z.object({
        seat_key: z.string().nullable(),
        title: z.string(),
        kind: SetupSeatKind,
        de_membership_id: Uuid.nullable(),
        status: z.string(),
        kpis: z.array(ReviewKpi),
        runs: z.int().min(0),
        failed: z.int().min(0),
        cost_micros: z.int(),
      }),
    ),
    failures: z.object({
      runs: z.int().min(0),
      failed: z.int().min(0),
      by_kind: z.record(z.string(), z.int()),
    }),
    usage: z.object({ cost_micros: z.int(), currency: z.string() }),
    open_recoveries: z.array(
      z.object({
        seat_key: z.string(),
        title: z.string(),
        status: z.string(),
        step: z.string(),
        why: z.string().nullable(),
      }),
    ),
  })
  .meta({ id: "OnboardingReviewMetrics" });

/** The operator's review screen: what was collected and the summary the client may be shown. */
export const OnboardingReviewView = z
  .object({
    id: Uuid,
    case_id: Uuid,
    kind: OnboardingReviewKind,
    status: OnboardingReviewStatus,
    due_at: Timestamp,
    collected_at: Timestamp.nullable(),
    reviewed_at: Timestamp.nullable(),
    metrics: OnboardingReviewMetrics.nullable(),
    /** Client-shareable: plain words from the numbers above, from the HTVA team. */
    client_summary: z.string().nullable(),
    /** The operator's own notes (never shown to the client). */
    operator_note: z.string().nullable(),
  })
  .meta({ id: "OnboardingReviewView" });
export type OnboardingReviewView = z.infer<typeof OnboardingReviewView>;

const INTERNAL_NAME = /skillsmith/i;
export const OnboardingReviewUpdate = z
  .strictObject({
    client_summary: z
      .string()
      .trim()
      .max(4000)
      .refine((s) => !INTERNAL_NAME.test(s), "the client summary must not name an internal agent")
      .optional(),
    operator_note: z.string().trim().max(4000).optional(),
    /** Marks the review gone through (needs it collected). */
    reviewed: z.boolean().optional(),
  })
  .meta({ id: "OnboardingReviewUpdate" });

/** `client_intake` (MCP): the onboarding answers a DE reads for its own seat. Read-only. */
export const SeatIntakeView = z
  .object({
    seat_id: Uuid,
    seat_title: z.string(),
    /** The proposal seat this seat was deployed from (null: not seated by onboarding). */
    onboarding_seat: z
      .object({
        key: z.string(),
        title: z.string(),
        kpis: z.array(z.string()),
        first_goals: z.array(z.string()),
      })
      .nullable(),
    /** The answers the seat rests on, then the business context every seat reads. */
    answers: z.array(
      z.object({
        key: z.string(),
        label: z.string(),
        value: FlowAnswer,
        /** Cited for this seat (not only context). */
        for_seat: z.boolean(),
      }),
    ),
  })
  .meta({ id: "SeatIntakeView" });
export type SeatIntakeView = z.infer<typeof SeatIntakeView>;

const OperatorCompanyParams = z.object({ company_id: Uuid });
const intake = "/v1/companies/{co}/onboarding/intake/{token}";
const link = { feature: "core", tag: "operator", access: "assessment_token" } as const;
const op = { feature: "core", tag: "operator", access: "operator" } as const;
const opCase = "/v1/operator/companies/{company_id}/onboarding";
const CompanyCoParams = z.object({ co: Uuid });
const coCase = "/v1/companies/{co}/onboarding";
const coOp = { feature: "core", tag: "operator", access: "operator" } as const;

export const clientOnboardingRoutes = {
  "operator.onboarding.get": defineRoute({
    feature: "core",
    tag: "operator",
    access: "operator",
    id: "operator.onboarding.get",
    method: "get",
    path: "/v1/operator/companies/{company_id}/onboarding",
    summary:
      "A client's onboarding case: plan, deadline, missing intake, follow-ups, timeline (CLIENT_ONBOARDING)",
    params: OperatorCompanyParams,
    response: { status: 200, schema: OnboardingCaseView },
    errors: ["not_found"],
    emits: [],
  }),
  "onboarding.intake.get": defineRoute({
    ...link,
    id: "onboarding.intake.get",
    method: "get",
    path: intake,
    summary: "The client's onboarding intake, by its link (CLIENT_ONBOARDING)",
    params: OnboardingTokenParams,
    response: { status: 200, schema: OnboardingIntakeView },
    errors: ["not_found"],
    emits: [],
  }),
  "onboarding.intake.saveStep": defineRoute({
    ...link,
    id: "onboarding.intake.saveStep",
    method: "put",
    path: `${intake}/steps/{step}`,
    summary: "Save one step of the onboarding intake, by its link (save and resume)",
    params: OnboardingStepParams,
    body: OnboardingStepSave,
    // The case's own rows and its timeline (`cell.onboarding_event`); no company event yet (phase
    // 2 binds the answers into the CEO seat, which emits there).
    safe: true,
    response: { status: 200, schema: OnboardingIntakeView },
    errors: ["not_found", "validation_failed", "conflict"],
    emits: [],
  }),
  "onboarding.intake.submit": defineRoute({
    ...link,
    id: "onboarding.intake.submit",
    method: "post",
    path: `${intake}/submit`,
    summary: "Submit the onboarding intake: the follow-up sequence stops",
    params: OnboardingTokenParams,
    // As saveStep: the case, its schedule and its timeline only.
    safe: true,
    response: { status: 200, schema: OnboardingIntakeView },
    errors: ["not_found", "validation_failed", "conflict"],
    emits: [],
  }),
  // ONBOARDING-P2 ------------------------------------------------------------------------------
  // Every write below touches the onboarding rows only (the case, the proposal, the seat
  // decisions, the timeline) and the HQ assignment: no company event, hence `safe`.
  "operator.onboarding.proposal.get": defineRoute({
    ...op,
    id: "operator.onboarding.proposal.get",
    method: "get",
    path: `${opCase}/proposal`,
    summary:
      "The client's setup proposal: every version, the owner's decisions, the approved seats",
    params: OperatorCompanyParams,
    response: { status: 200, schema: SetupProposalOperatorView },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.onboarding.proposal.edit": defineRoute({
    ...op,
    id: "operator.onboarding.proposal.edit",
    method: "put",
    path: `${opCase}/proposal`,
    summary:
      "Edit the setup proposal under review (after a version was sent: a new version to review)",
    params: OperatorCompanyParams,
    body: SetupProposalBody,
    safe: true,
    response: { status: 200, schema: SetupProposalOperatorView },
    errors: ["not_found", "validation_failed", "conflict"],
    emits: [],
  }),
  "operator.onboarding.proposal.send": defineRoute({
    ...op,
    id: "operator.onboarding.proposal.send",
    method: "post",
    path: `${opCase}/proposal/send`,
    summary: "Send the reviewed proposal to the client (the version is frozen from then on)",
    params: OperatorCompanyParams,
    safe: true,
    response: { status: 200, schema: SetupProposalOperatorView },
    errors: ["not_found", "validation_failed", "conflict"],
    emits: [],
  }),
  "operator.onboarding.assistants.list": defineRoute({
    ...op,
    id: "operator.onboarding.assistants.list",
    method: "get",
    path: `${opCase}/assistants`,
    summary: "The HQ setup assistants and the one serving this client",
    params: OperatorCompanyParams,
    response: { status: 200, schema: SetupAssistantList },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.onboarding.assistant.assign": defineRoute({
    ...op,
    id: "operator.onboarding.assistant.assign",
    method: "put",
    path: `${opCase}/assistant`,
    summary: "Assign an HQ setup assistant to this client (any previous one is revoked)",
    params: OperatorCompanyParams,
    body: SetupAssistantAssign,
    safe: true,
    response: { status: 200, schema: SetupAssistantList },
    errors: ["not_found", "conflict"],
    emits: [],
  }),
  "operator.onboarding.close": defineRoute({
    ...op,
    id: "operator.onboarding.close",
    method: "post",
    path: `${opCase}/close`,
    summary: "End onboarding (go-live or cancel): the setup assistant's access is revoked",
    params: OperatorCompanyParams,
    body: OnboardingClose,
    safe: true,
    response: { status: 200, schema: OnboardingCaseView },
    errors: ["not_found", "conflict"],
    emits: [],
  }),
  "onboarding.proposal.get": defineRoute({
    ...link,
    id: "onboarding.proposal.get",
    method: "get",
    path: `${intake}/proposal`,
    summary: "The setup proposal the HTVA team sent, presented by the client's setup assistant",
    params: OnboardingTokenParams,
    response: { status: 200, schema: SetupProposalClientView },
    errors: ["not_found"],
    emits: [],
  }),
  "onboarding.proposal.decideSeat": defineRoute({
    ...link,
    id: "onboarding.proposal.decideSeat",
    method: "put",
    path: `${intake}/proposal/seats/{seat_key}`,
    summary: "The owner approves, changes or defers one proposed seat",
    params: OnboardingSeatParams,
    body: SetupSeatDecide,
    safe: true,
    response: { status: 200, schema: SetupProposalClientView },
    errors: ["not_found", "validation_failed", "conflict"],
    emits: [],
  }),
  // ONBOARDING-P3 ------------------------------------------------------------------------------
  // In the client's company scope (the API login): the operator acts there as its agency
  // operator, so the chat post, the seats and the DEs are the company's own writes and events.
  "onboarding.present": defineRoute({
    ...coOp,
    id: "onboarding.present",
    method: "post",
    path: `${coCase}/present`,
    summary:
      "The setup assistant presents the sent proposal (or offers a call) in the client's support channel",
    params: CompanyCoParams,
    body: OnboardingPresent,
    response: { status: 200, schema: OnboardingPresented },
    errors: ["not_found", "conflict"],
    emits: ["message.created", "topic.created"],
  }),
  "onboarding.seating.get": defineRoute({
    ...coOp,
    id: "onboarding.seating.get",
    method: "get",
    path: `${coCase}/seating`,
    summary: "The seating run: each approved seat's status, the template tickets, the DE allowance",
    params: CompanyCoParams,
    response: { status: 200, schema: SeatingView },
    errors: ["not_found"],
    emits: [],
  }),
  "onboarding.seating.deploy": defineRoute({
    ...coOp,
    id: "onboarding.seating.deploy",
    method: "post",
    path: `${coCase}/seating`,
    summary:
      "Deploy seats: plan the run over the seats the owner approved (refused, blocked and failed ones again)",
    params: CompanyCoParams,
    // The run's own rows and the case timeline only; the seats and DEs are made by `next`.
    safe: true,
    response: { status: 200, schema: SeatingView },
    errors: ["not_found", "conflict"],
    emits: [],
  }),
  "onboarding.seating.next": defineRoute({
    ...coOp,
    id: "onboarding.seating.next",
    method: "post",
    path: `${coCase}/seating/next`,
    summary:
      "Run the next seat: open a person's seat, or a DE seat and its DE from its template (or a template ticket)",
    params: CompanyCoParams,
    response: { status: 200, schema: SeatingView },
    errors: ["not_found", "conflict", "de_allowance_reached", "supervisor_required"],
    emits: [
      "role.created",
      "seat.created",
      "membership.created",
      "digital_employee.created",
      "seat.assigned",
      "access.granted",
      "seat_metric.changed",
      "target_rule.changed",
      // not team.updated: the DE's seat is opened in the same step, so it is in no team yet
      "metric_def.created",
      // the recovery contract's later steps of a DE seat
      "digital_employee.provision_requested",
      "digital_employee.activated",
    ],
  }),
  // lane A (beta shared contracts §3.2)
  "onboarding.readiness.check": defineRoute({
    ...coOp,
    id: "onboarding.readiness.check",
    method: "post",
    path: `${coCase}/readiness`,
    summary:
      "Check readiness now: the six handover checks from their evidence (the job runs it every 5 minutes)",
    params: CompanyCoParams,
    // The case's cached checks, its seats' ready marks and the timeline's evidence lines only.
    safe: true,
    response: { status: 200, schema: OnboardingReadinessView },
    errors: ["not_found"],
    emits: [],
  }),
  "onboarding.handover": defineRoute({
    ...coOp,
    id: "onboarding.handover",
    method: "post",
    path: `${coCase}/handover`,
    summary:
      "Hand over: needs every readiness check; ends onboarding and revokes the setup assistant (again on a retry)",
    params: CompanyCoParams,
    safe: true,
    response: { status: 200, schema: OnboardingReadinessView },
    errors: ["not_found", "conflict"],
    emits: [],
  }),
  // ONBOARDING-P4 ------------------------------------------------------------------------------
  "operator.onboarding.dispatch.list": defineRoute({
    ...op,
    id: "operator.onboarding.dispatch.list",
    method: "get",
    path: `${opCase}/dispatch`,
    summary:
      "The client's system address and what the HQ dispatcher did with each message to it (class, route)",
    params: OperatorCompanyParams,
    response: { status: 200, schema: DispatchListView },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.onboarding.review.get": defineRoute({
    ...op,
    id: "operator.onboarding.review.get",
    method: "get",
    path: `${opCase}/review`,
    summary:
      "The 30-day review: KPIs per seat, failures, usage cost, open recoveries, the client summary",
    params: OperatorCompanyParams,
    response: { status: 200, schema: OnboardingReviewView },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.onboarding.review.update": defineRoute({
    ...op,
    id: "operator.onboarding.review.update",
    method: "put",
    path: `${opCase}/review`,
    summary: "Edit the 30-day review's client summary and notes, or mark it reviewed",
    params: OperatorCompanyParams,
    body: OnboardingReviewUpdate,
    // The review row and the case timeline only.
    safe: true,
    response: { status: 200, schema: OnboardingReviewView },
    errors: ["not_found", "validation_failed", "conflict"],
    emits: [],
  }),
  "onboarding.seatIntake": defineRoute({
    feature: "core",
    tag: "operator",
    access: "member",
    id: "onboarding.seatIntake",
    method: "get",
    path: `${coCase}/seat-intake`,
    summary:
      "The onboarding intake answers for the caller's own seat (a DE's `client_intake` tool); read-only",
    params: CompanyCoParams,
    response: { status: 200, schema: SeatIntakeView },
    errors: ["not_found"],
    emits: [],
  }),
};
